import { Tags } from 'lucide-react';
import { useParams, useSearchParams } from 'react-router-dom';
import { SearchParam } from '@common/ts/enums';
import { useGetInterviewQuestions, useGetQuestionCategories } from '@/reactQuery/get';
import { TranscriptsQuantification } from '@/types';

interface ConversationCategoriesProps {
  quantification?: TranscriptsQuantification;
}

export default function ConversationCategories({ quantification }: ConversationCategoriesProps) {
  const { project_id: projectId } = useParams();
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);

  const { data: interviewQuestions } = useGetInterviewQuestions(filterId, projectId);

  const categories = quantification?.categories;
  if (!categories || !interviewQuestions) return null;

  // Get question IDs that have category assignments
  const questionIdsWithCategories = Object.keys(categories).filter((qId) => {
    const val = categories[qId];
    return Array.isArray(val) ? val.length > 0 : !!val;
  });

  if (questionIdsWithCategories.length === 0) return null;

  return (
    <div className="border-t border-neutral-100 mt-4 pt-4">
      <div className="flex items-center gap-2 mb-3">
        <Tags className="h-4 w-4 text-neutral-400" />
        <span className="text-sm font-semibold text-neutral-900">Categories</span>
      </div>
      <div className="flex flex-col gap-3">
        {questionIdsWithCategories.map((questionId) => {
          const question = interviewQuestions.find((q) => q.id === questionId);
          if (!question) return null;

          const categoryIds = categories[questionId];
          const ids = Array.isArray(categoryIds) ? categoryIds : [categoryIds];

          return (
            <QuestionCategoryRow
              key={questionId}
              projectId={projectId}
              questionId={questionId}
              questionText={question.question}
              categoryIds={ids}
              filterId={filterId}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Per-question row — calls useGetQuestionCategories (cached via Tanstack Query).
 *
 * N+1 API calls: each row fires one GET /perquestion/:questionId/categories request.
 * This is intentional because React hooks cannot be called inside loops, so each
 * QuestionCategoryRow component owns its own hook invocation.
 *
 * Performance mitigation: useGetQuestionCategories uses staleTime: StaleTime.LONG
 * (when enablePolling is false, which is always the case here). This means repeated
 * views of the same conversation panel reuse the Tanstack Query cache and do not
 * trigger additional network requests during the same session.
 *
 * Bulk endpoint: as of 2026-03-31 the backend (results.controller.ts) has no
 * GET /categories?questionIds[]=...&questionIds[]=... bulk endpoint. Creating one
 * is a backend task and out of scope for this frontend component.
 */
function QuestionCategoryRow({
  projectId,
  questionId,
  questionText,
  categoryIds,
  filterId,
}: {
  projectId?: string;
  questionId: string;
  questionText: string;
  categoryIds: string[];
  filterId: string | null;
}) {
  const { data: categoryData } = useGetQuestionCategories(filterId, projectId, questionId);

  // Build lookup from both single and multi category definitions
  const allDefs = [...(categoryData?.categories || []), ...(categoryData?.categories_multi || [])];
  const defMap = new Map(allDefs.map((c) => [c.id, c]));

  // Resolve category IDs to definitions
  const resolved = categoryIds.map((id) => defMap.get(id)).filter(Boolean);

  if (resolved.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-neutral-500 line-clamp-1">{questionText}</span>
      <div className="flex flex-wrap gap-1">
        {resolved.map((cat) => (
          <span
            key={cat!.id}
            className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
            style={{ backgroundColor: cat!.color, color: getContrastColor(cat!.color) }}
            title={cat!.prompt}
          >
            {cat!.label}
          </span>
        ))}
      </div>
    </div>
  );
}

/** Returns black or white text depending on background luminance */
function getContrastColor(hex: string): string {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#374151' : '#ffffff';
}
